import { FormikProps, useFormikContext } from 'formik'

import { useStyles } from '../PrivateOfferPlans/PrivateOfferPlans.styles'

import {
  GCPInstallments,
  PaymentType,
  PlanTypeGCP,
  PrivateOffer,
} from '../../../../../modules/privateOffer/reducer'
import clsx from 'clsx'
import { TableCell, Typography } from '@mui/material'
import {
  FieldTitle,
  LabraToolTip,
  SwitchV2,
  TableComponentV2,
  TextInput,
} from '@labrav/react-components'
import { InfoOutlined } from '@mui/icons-material'
import { getDiscountedPriceValue } from '@labrav/common'
import { get } from 'lodash'
import {
  getValidValueBetweenMinMax,
  getValidValueBetweenMinMaxFloat,
} from '@labrav/common'
import { ReactNode, useMemo } from 'react'

const MetricMapping: Record<string, string> = {
  Metrics: 'label',
  'Public price (USD)': 'price',
  'Percentage of discount on usage': 'discount',
  'Discounted price (USD)': 'discountedPrice',
}

export const MetricDiscount = () => {
  const {
    values,
    errors: error,
    touched,
    handleBlur,
    setFieldValue,
  } = useFormikContext() as FormikProps<PrivateOffer>
  const { classes } = useStyles()
  const customHeaderMapping = useMemo(() => {
    let customHeader = {} as Record<string, ReactNode>
    Object.keys(MetricMapping).forEach(key => {
      customHeader = {
        ...customHeader,
        [key]: (
          <TableCell
            className={classes.tableHead}
            key={MetricMapping[key]}
            data-testid={`header-${MetricMapping[key]}`}
          >
            {MetricMapping[key] === 'discount' ? (
              <FieldTitle title={key} isRequired={true} />
            ) : (
              key
            )}
          </TableCell>
        ),
      }
    })
    return customHeader
  }, [])

  const { plan } = values
  const shouldShowInstallmentDimensions =
    plan?.planType === PlanTypeGCP.FLAT_FEE_USAGE &&
    plan?.paymentType === PaymentType.CUSTOM
  const handleSwitchChange = (e: { target: { checked: boolean } }) => {
    setFieldValue('plan.individualDiscount', e.target.checked)
  }

  const handleDiscountChange =
    (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setFieldValue(
        `plan.metricPricing[${index}].discount`,
        getValidValueBetweenMinMaxFloat(e.target.value as string, 1, 100)
      )
    }

  const metricMappingDataObjects = (plan?.metricPricing || []).map(
    (planDimension, index) => ({
      label: planDimension?.label,
      price: (planDimension?.price || '') as string,
      discount: (
        <TextInput
          isRequired={true}
          keyName={`plan.metricPricing[${index}].discount`}
          title="Add percentage (%)"
          value={planDimension?.discount || ''}
          updatedValue={planDimension?.discount}
          onChange={handleDiscountChange(index)}
          type="number"
          onBlur={handleBlur}
          isError={
            !!(
              get(touched, `plan.metricPricing[${index}].discount`, false) &&
              get(error, `plan.metricPricing[${index}].discount`, false)
            )
          }
          errorText={
            (!!get(touched, `plan.metricPricing[${index}].discount`, false) &&
              get(error, `plan.metricPricing[${index}].discount`, ' ')) ||
            ' '
          }
          isReadOnly={false}
          placeHolder="Add percentage (%)"
          variant="regularV2"
          keyToPrevent={['+', '-', 'e']}
          minNumber={1}
          maxNumber={100}
        />
      ),
      discountedPrice: `${getDiscountedPriceValue({
        price: planDimension.price as number,
        discount: planDimension.discount,
      })}`,
    })
  )

  return (
    <div>
      <div
        className={clsx(
          classes.subHeadingContainer,
          classes.companyDetailSubHead,
          classes.marginTop
        )}
        data-testid="metric-heading"
      >
        <Typography
          className={classes.subHeading}
          data-testid="details-subheading"
        >
          Metric discounts (Optional)
        </Typography>
        <LabraToolTip
          tooltipPlacement={'right'}
          label={
            'Add discount for each metric or offer an overall discount on all.'
          }
        >
          <InfoOutlined className={classes.infoIcon} />
        </LabraToolTip>
      </div>
      <div
        className={classes.descriptionContainer}
        data-testid="description-info"
      >
        <Typography className={classes.subheadingInfoText}>
          You can apply discounts on indivisual metrics
        </Typography>
      </div>
      <div
        className={clsx(classes.descriptionContainer, classes.metricDiscount)}
        data-testid="description-info-discount"
      >
        <SwitchV2
          checked={!!values.plan?.individualDiscount}
          onChange={handleSwitchChange}
          data-testid={'individualDiscount'}
        />
        <div>
          <Typography
            className={clsx(
              classes.subheadingInfoText,
              classes.metricDiscountText
            )}
          >
            Enable metric discounts
          </Typography>
        </div>
      </div>
      {plan?.individualDiscount && (
        <div>
          {shouldShowInstallmentDimensions ? (
            <>
              {plan?.installmentInfo?.map((installmentItem, index) => {
                return (
                  <div>
                    <Typography
                      className={clsx(
                        classes.subHeading,
                        classes.installmentMetricRow
                      )}
                      data-testid="details-subheading-installment-title"
                    >
                      Instalment {index + 1}
                    </Typography>
                    <InstallmentsMetricDiscountRow
                      installmentIndex={index}
                      installment={installmentItem}
                      customHeader={customHeaderMapping}
                    />
                  </div>
                )
              })}
            </>
          ) : (
            <TableComponentV2
              objects={metricMappingDataObjects}
              mapping={MetricMapping}
              customHeader={customHeaderMapping}
              count={metricMappingDataObjects.length}
              fetchMoreData={() => void 0}
              onChangeCheckbox={() => void 0}
              selectedItem={[]}
              showCheckbox={false}
              isBasicTable={true}
              keyName="table-basic"
            />
          )}
        </div>
      )}
    </div>
  )
}

const InstallmentsMetricDiscountRow = ({
  installmentIndex,
  installment,
  customHeader,
}: {
  installmentIndex: number
  installment: GCPInstallments
  customHeader: Record<string, ReactNode>
}) => {
  const {
    errors: error,
    touched,
    handleBlur,
    setFieldValue,
  } = useFormikContext() as FormikProps<PrivateOffer>

  const handleDiscountChange =
    (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setFieldValue(
        `plan.installmentInfo[${installmentIndex}].metricPricing[${index}].discount`,
        getValidValueBetweenMinMax(e.target.value as string, 1, 100)
      )
    }

  const metricMappingDataObjects = (installment?.metricPricing || []).map(
    (installmentDimension, index) => ({
      label: installmentDimension?.label,
      price: (installmentDimension?.price || '') as string,
      discount: (
        <TextInput
          isRequired={true}
          keyName={`plan.installmentInfo[${installmentIndex}].metricPricing[${index}].discount`}
          title="Add percentage (%)"
          value={installmentDimension?.discount || ''}
          updatedValue={installmentDimension?.discount}
          onChange={handleDiscountChange(index)}
          type="number"
          onBlur={handleBlur}
          isError={
            !!(
              get(
                touched,
                `plan.installmentInfo[${installmentIndex}].metricPricing[${index}].discount`,
                false
              ) &&
              get(
                error,
                `plan.installmentInfo[${installmentIndex}].metricPricing[${index}].discount`,
                false
              )
            )
          }
          errorText={
            (!!get(
              touched,
              `plan.installmentInfo[${installmentIndex}].metricPricing[${index}].discount`,
              false
            ) &&
              get(
                error,
                `plan.installmentInfo[${installmentIndex}].metricPricing[${index}].discount`,
                ' '
              )) ||
            ' '
          }
          isReadOnly={false}
          placeHolder="Add percentage (%)"
          variant="regularV2"
          keyToPrevent={['+', '-', 'e']}
          minNumber={1}
          maxNumber={100}
        />
      ),
      discountedPrice: getDiscountedPriceValue({
        price: installmentDimension.price as number,
        discount: installmentDimension.discount,
      }) as unknown as string,
    })
  )

  return (
    <TableComponentV2
      objects={metricMappingDataObjects}
      mapping={MetricMapping}
      customHeader={customHeader}
      count={metricMappingDataObjects.length}
      fetchMoreData={() => void 0}
      onChangeCheckbox={() => void 0}
      selectedItem={[]}
      showCheckbox={false}
      isBasicTable={true}
      keyName="table-basic"
    />
  )
}
